/**
 * State-machine updaters for the player robot.
 *
 * Extracted from `Robot.ts` as free functions. Each `update*` function
 * takes the `Robot` as its first argument and mutates chassis + nav +
 * weaponry state for one tick. `Robot.update(dt)` dispatches on
 * `robot.state` and calls exactly one of these per frame.
 *
 * The functions also read the per-frame `_cachedNearestEnemy /
 * _cachedNearestLoot` that `Robot.update` populates once at the top of
 * the tick, and `findNearest` is exported for that caching pass.
 */

import { type Entity, type Enemy, type LootChest } from './Entity';
import type { Robot } from './Robot';
import { wrapAngle } from './mathUtils';
import { LOOT_RANGE, TURRET_AIM_THRESHOLD } from './RobotConstants';
import {
  createEnemyBomb, createEnemyBullet, createExplosion,
  createMeleeSlash, createMuzzleFlash, createRobotHit,
} from './Projectile';
import { hasStatusEffect } from './StatusEffect';
import { generateLoot } from '../upgrades/UpgradeData';
import { generateWeaponLoot } from '../upgrades/WeaponData';
import { rollScore, scoreBand } from '../upgrades/Quality';
import { generateItemName } from '../upgrades/ItemNames';
import { trackLootPickup } from '../achievements/Hooks';

/**
 * Pick the nearest in-range, line-of-sight `enemy` or `loot` entity.
 * Also handles swarm-enemy selection: when looking for enemies and a
 * swarm target is closer than any regular enemy, sets
 * `robot.swarmTargetIdx` and returns `null` (combat uses the index
 * directly). Clears `swarmTargetIdx` when a regular enemy wins.
 */
export function findNearest(robot: Robot, type: 'enemy' | 'loot'): Entity | null {
  if (type === 'enemy' && robot.passive) {
    robot.swarmTargetIdx = -1;
    return null;
  }

  let best: Entity | null = null;
  let bestDist = robot.detectionRadius;

  for (const e of robot.entities) {
    if (e.type !== type || !e.alive) continue;
    const d = Math.hypot(e.x - robot.x, e.y - robot.y);
    if (d < bestDist && robot.hasLineOfSight(e.x, e.y)) {
      bestDist = d;
      best = e;
    }
  }

  if (type === 'enemy' && robot.swarmManager && robot.swarmManager.aliveCount > 0) {
    const swarmIdx = robot.swarmManager.findNearest(robot.x, robot.y, bestDist);
    if (swarmIdx >= 0) {
      const sx = robot.swarmManager.x[swarmIdx]!;
      const sy = robot.swarmManager.y[swarmIdx]!;
      if (robot.hasLineOfSight(sx, sy)) {
        robot.swarmTargetIdx = swarmIdx;
        return null;
      }
    }
  }

  if (type === 'enemy') {
    robot.swarmTargetIdx = -1;
  }
  return best;
}

/** Snap the robot out of combat — used by swarm-target-lost and passive-mode exits. */
export function disengageCombat(robot: Robot): void {
  robot.state = robot.marker ? 'moving_to_marker' : 'exploring';
  robot.target = null;
  robot.swarmTargetIdx = -1;
  robot.combatTimer = 0;
  robot.nav.clearPath();
}

/**
 * Exploring tick: prioritise threat → loot → pathing. If no frontier
 * target exists, `planExplorationPath` falls back to random-walk and
 * ultimately heads home to end the run cleanly via energy drain.
 */
export function updateExploring(robot: Robot, dt: number): void {
  const nearbyEnemy = robot._cachedNearestEnemy;
  if (nearbyEnemy) {
    robot.state = 'combat';
    robot.target = nearbyEnemy;
    robot.swarmTargetIdx = -1;
    robot.combatTimer = 0;
    robot.nav.clearPath();
    const e = nearbyEnemy as Enemy;
    robot.log(`Enemy detected (HP: ${Math.ceil(e.maxHp)})`, 0xff6644);
    return;
  }
  if (robot.swarmTargetIdx >= 0) {
    robot.state = 'combat';
    robot.target = null;
    robot.combatTimer = 0;
    robot.nav.clearPath();
    robot.log('Swarm incoming!', 0xff4444);
    return;
  }

  const nearbyLoot = robot._cachedNearestLoot;
  if (nearbyLoot) {
    robot.state = 'looting';
    robot.target = nearbyLoot;
    robot.log('Loot chest found', 0xffcc44);
    robot.lootProgress = 0;
    robot.nav.clearPath();
    return;
  }

  robot.nav.updateStuckDetection(robot, dt);

  robot.nav.pathCooldown -= dt;
  if (robot.nav.needsNewPath() && robot.nav.pathCooldown <= 0) {
    robot.nav.planExplorationPath(robot);
    robot.nav.pathCooldown = 0.3;
  }

  robot.nav.followSpline(robot, dt, robot.speed);
}

/**
 * Combat tick: passive-mode disengages immediately. Swarm-target
 * branch defers to `updateSwarmCombat`. Otherwise track the main
 * target: approach until in weapon range with LOS, then evasive
 * strafe + fire, and let the enemy counter-attack.
 */
export function updateCombat(robot: Robot, dt: number): void {
  if (robot.passive) {
    disengageCombat(robot);
    return;
  }

  if (robot.swarmTargetIdx >= 0) {
    updateSwarmCombat(robot, dt);
    return;
  }

  const enemy = robot.target?.type === 'enemy' ? robot.target as Enemy : null;

  if (!enemy || !enemy.alive) {
    robot.state = robot.marker ? 'moving_to_marker' : 'exploring';
    robot.target = null;
    robot.combatTimer = 0;
    robot.nav.clearPath();
    return;
  }

  const dx = enemy.x - robot.x;
  const dy = enemy.y - robot.y;
  const dist = Math.hypot(dx, dy);
  const hasLOS = robot.hasLineOfSight(enemy.x, enemy.y);

  if (!hasLOS && dist > robot.detectionRadius) {
    robot.state = robot.marker ? 'moving_to_marker' : 'exploring';
    robot.target = null;
    robot.combatTimer = 0;
    robot.nav.clearPath();
    return;
  }

  const combatRange = robot.weaponry.getEffectiveCombatRange();

  if (dist > combatRange || !hasLOS) {
    robot.nav.approachEntity(robot, dt, enemy.x, enemy.y, robot.combatApproachSpeed);
    return;
  }

  // In range with LOS — strafe + fire + resolve enemy counter-attack.
  robot.nav.performEvasiveStrafe(robot, dt, enemy.x, enemy.y, dist, combatRange);

  const turretToTarget = Math.atan2(dy, dx);
  const aimDiff = wrapAngle(turretToTarget - robot.turretAngle);
  const turretAimed = Math.abs(aimDiff) < TURRET_AIM_THRESHOLD;

  robot.weaponry.fireWeapons(robot, dt, enemy, turretAimed);

  // Unarmed fallback — tick melee "punches" at the base attack speed.
  if (robot.weaponry.weapons.length === 0) {
    robot.combatTimer += dt;
    if (robot.combatTimer >= robot.attackSpeed) {
      robot.combatTimer -= robot.attackSpeed;
      robot.weaponry.applyDamageToEnemy(robot, enemy, robot.weaponDamage, 'melee');
      robot.vfx.push(createMeleeSlash(enemy.x, enemy.y, 0x888888));
    }
  }

  // Enemy counter-attack. Stunned enemies and shielders never fire.
  if (hasStatusEffect(enemy, 'stun') || enemy.enemyKind === 'shielder') return;

  enemy.attackTimer += dt;
  if (enemy.attackTimer < enemy.attackSpeed || dist > enemy.attackRange) return;
  enemy.attackTimer -= enemy.attackSpeed;

  if (enemy.isBoss && enemy.enemyKind === 'turret') {
    runSiegeCoreBossAttack(robot, enemy, dist);
  } else {
    robot.enemyProjectiles.push(createEnemyBullet(enemy.x, enemy.y, robot.x, robot.y, enemy.damage));
    robot.vfx.push(createMuzzleFlash(enemy.x, enemy.y, 0xff4444));
    void robot.sound?.playSound('/sound/ballistic-weapon.mp3', 0.4);
  }
}

/** Siege Core boss cycles through 3 attack modes (burst / bomb salvo / shockwave). */
function runSiegeCoreBossAttack(robot: Robot, enemy: Enemy, dist: number): void {
  enemy.bossPhase = (enemy.bossPhase + 1) % 3;
  switch (enemy.bossPhase) {
    case 0: {
      // Rapid burst — 4 bullets in a tight spread.
      for (let b = 0; b < 4; b++) {
        const spread = (b - 1.5) * 0.12;
        const angle = Math.atan2(robot.y - enemy.y, robot.x - enemy.x) + spread;
        const tx = enemy.x + Math.cos(angle) * dist;
        const ty = enemy.y + Math.sin(angle) * dist;
        robot.enemyProjectiles.push(createEnemyBullet(enemy.x, enemy.y, tx, ty, enemy.damage * 0.6));
      }
      robot.vfx.push(createMuzzleFlash(enemy.x, enemy.y, 0xff4444));
      void robot.sound?.playSound('/sound/ballistic-weapon.mp3', 0.6);
      break;
    }
    case 1: {
      // Bomb barrage — 2 AoE bombs around the robot.
      for (let b = 0; b < 2; b++) {
        const ox = (Math.random() - 0.5) * 2;
        const oy = (Math.random() - 0.5) * 2;
        robot.enemyProjectiles.push(
          createEnemyBomb(enemy.x, enemy.y, robot.x + ox, robot.y + oy, enemy.damage * 0.8, 1.8),
        );
      }
      robot.vfx.push(createMuzzleFlash(enemy.x, enemy.y, 0x9933cc));
      void robot.sound?.playSound('/sound/ballistic-weapon.mp3', 0.5);
      break;
    }
    case 2: {
      // Ground shockwave — AoE centered on the boss, damage if robot inside.
      const shockRadius = 3.0;
      const d = Math.hypot(robot.x - enemy.x, robot.y - enemy.y);
      if (d <= shockRadius) {
        const dmg = enemy.damage * 1.2 * (1 - robot.damageReduction);
        robot.structure -= dmg;
        robot.damageTakenThisRun += dmg;
        if (robot.structure < robot.maxStructure * 0.1) robot.lowHpEverThisRun = true;
        robot.floatText(`${Math.round(enemy.damage * 1.2)}`, 0xff4444, robot.x, robot.y - 0.3);
        robot.vfx.push(createRobotHit(robot.x, robot.y));
      }
      robot.vfx.push(createExplosion(enemy.x, enemy.y, shockRadius, 0xff6600));
      void robot.sound?.playSound('/sound/explosion.mp3', 2.0);
      break;
    }
  }
}

/**
 * Swarm-combat tick: simplified combat path that targets by swarm
 * index rather than Entity reference. Every ~0.5s re-evaluates the
 * nearest swarm enemy and bails to regular combat if a conventional
 * enemy is now closer.
 */
export function updateSwarmCombat(robot: Robot, dt: number): void {
  const sm = robot.swarmManager;
  if (!sm || robot.swarmTargetIdx < 0 || !sm.alive[robot.swarmTargetIdx]) {
    if (sm && sm.aliveCount > 0) {
      const newIdx = sm.findNearest(robot.x, robot.y, robot.detectionRadius);
      if (newIdx >= 0 && robot.hasLineOfSight(sm.x[newIdx]!, sm.y[newIdx]!)) {
        robot.swarmTargetIdx = newIdx;
      } else {
        disengageCombat(robot);
        return;
      }
    } else {
      disengageCombat(robot);
      return;
    }
  }

  const idx = robot.swarmTargetIdx;
  const tx = sm.x[idx]!;
  const ty = sm.y[idx]!;
  const dx = tx - robot.x;
  const dy = ty - robot.y;
  const dist = Math.hypot(dx, dy);
  const hasLOS = robot.hasLineOfSight(tx, ty);

  if (!hasLOS && dist > robot.detectionRadius) {
    disengageCombat(robot);
    return;
  }

  const combatRange = robot.weaponry.getEffectiveCombatRange();

  if (dist > combatRange || !hasLOS) {
    robot.nav.approachEntity(robot, dt, tx, ty, robot.combatApproachSpeed);
  } else {
    robot.nav.performEvasiveStrafe(robot, dt, tx, ty, dist, combatRange);

    const turretToTarget = Math.atan2(dy, dx);
    const aimDiff = wrapAngle(turretToTarget - robot.turretAngle);
    const turretAimed = Math.abs(aimDiff) < TURRET_AIM_THRESHOLD;

    robot.weaponry.fireSwarmWeapons(robot, dt, idx, turretAimed);

    if (robot.weaponry.weapons.length === 0) {
      robot.combatTimer += dt;
      if (robot.combatTimer >= robot.attackSpeed) {
        robot.combatTimer -= robot.attackSpeed;
        sm.damage_enemy(idx, robot.weaponDamage);
        robot.vfx.push(createMeleeSlash(tx, ty, 0x888888));
      }
    }
  }

  // Re-target every ~0.5s; promote to regular-enemy combat if closer.
  robot.combatTimer += dt;
  if (robot.combatTimer >= 0.5) {
    robot.combatTimer = 0;
    const newIdx = sm.findNearest(robot.x, robot.y, robot.detectionRadius);
    if (newIdx >= 0) {
      robot.swarmTargetIdx = newIdx;
    }
    const nearestRegular = robot._cachedNearestEnemy;
    if (nearestRegular && nearestRegular.alive) {
      const rd = Math.hypot(nearestRegular.x - robot.x, nearestRegular.y - robot.y);
      if (newIdx >= 0) {
        const sd = Math.hypot(sm.x[newIdx]! - robot.x, sm.y[newIdx]! - robot.y);
        if (rd < sd) {
          robot.target = nearestRegular;
          robot.swarmTargetIdx = -1;
        }
      }
    }
  }
}

/**
 * Looting tick: approach the chest, then tick the loot-progress bar.
 * On completion, roll one upgrade / weapon drop scaled by distance
 * from base. Combat interrupts loot regardless of proximity.
 */
export function updateLooting(robot: Robot, dt: number): void {
  const chest = robot.target?.type === 'loot' ? robot.target as LootChest : null;

  if (!chest || !chest.alive) {
    robot.state = robot.marker ? 'moving_to_marker' : 'exploring';
    robot.target = null;
    robot.lootProgress = 0;
    robot.nav.clearPath();
    return;
  }

  const dist = Math.hypot(chest.x - robot.x, chest.y - robot.y);

  if (dist > LOOT_RANGE) {
    robot.nav.approachEntity(robot, dt, chest.x, chest.y, robot.speed);
    robot.lootProgress = 0;
    return;
  }

  robot.lootProgress += dt / chest.lootTime;
  if (robot.lootProgress >= 1) {
    robot.lootProgress = 1;
    robot.log(`Chest opened! +${chest.value}`, 0xffcc44);
    const chestDist = Math.abs(chest.x - robot.baseX) + Math.abs(chest.y - robot.baseY);
    if (Math.random() < 0.5) {
      const drop = generateWeaponLoot(chestDist, 1.0);
      if (drop) {
        const score = rollScore(chestDist, robot.qualityBonus);
        const name = generateItemName('weapon', drop.category, score);
        const band = scoreBand(score);
        robot.lootBag.push({ def: drop, item: { id: drop.id, score, name } });
        robot.log(`Found: ${name} (${band.label} · ${score})`, band.color);
        trackLootPickup(robot, score, false, drop.id);
      }
    } else {
      const drop = generateLoot(chestDist, 1.0);
      if (drop) {
        const score = rollScore(chestDist, robot.qualityBonus);
        const name = generateItemName('upgrade', drop.category, score);
        const band = scoreBand(score);
        robot.lootBag.push({ def: drop, item: { id: drop.id, score, name } });
        robot.log(`Found: ${name} (${band.label} · ${score})`, band.color);
        trackLootPickup(robot, score, false, null);
      }
    }
    chest.alive = false;
    robot.state = robot.marker ? 'moving_to_marker' : 'exploring';
    robot.target = null;
    robot.lootProgress = 0;
    robot.nav.clearPath();
  }

  const nearbyEnemy = robot._cachedNearestEnemy;
  if (nearbyEnemy) {
    robot.state = 'combat';
    robot.target = nearbyEnemy;
    robot.combatTimer = 0;
    robot.lootProgress = 0;
    robot.nav.clearPath();
  }
}

/**
 * Moving-to-marker tick: path toward the player's placed marker;
 * enemies interrupt (unless passive). Clears the marker on arrival
 * and returns to exploring.
 */
export function updateMovingToMarker(robot: Robot, dt: number): void {
  if (!robot.marker) {
    robot.state = 'exploring';
    robot.nav.clearPath();
    return;
  }

  if (!robot.passive) {
    const nearbyEnemy = robot._cachedNearestEnemy;
    if (nearbyEnemy) {
      robot.state = 'combat';
      robot.target = nearbyEnemy;
      robot.swarmTargetIdx = -1;
      robot.combatTimer = 0;
      robot.nav.clearPath();
      return;
    }
    if (robot.swarmTargetIdx >= 0) {
      robot.state = 'combat';
      robot.target = null;
      robot.combatTimer = 0;
      robot.nav.clearPath();
      return;
    }
  }

  const [mx, my] = robot.marker;
  const dist = Math.hypot(mx - robot.x, my - robot.y);

  if (dist < 1.0) {
    robot.marker = null;
    robot.state = 'exploring';
    robot.nav.clearPath();
    return;
  }

  robot.nav.approachEntity(robot, dt, mx, my, robot.speed);
}
